/**
 * Axios Interceptors Configuration
 * 
 * Handles:
 * - Request: Authentication token injection, custom headers
 * - Response: Logging, transformation
 * - Error: HTTP status code handling, token refresh, CORS errors
 * 
 * @module interceptors
 */

import { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { axiosInstance, DrupalAxiosRequestConfig } from '@core/config/axios.config';
import { useAuthStore } from '@store/auth.store';
import { getFastAPIBaseUrl } from '@services/proxy.service';
import { enqueueSnackbar } from 'notistack';

/**
 * Check if error is a CORS error (should never be retried)
 */
const isCorsError = (error: AxiosError): boolean => {
    // CORS errors typically have no response and specific error codes
    if (!error.response) {
        const message = error.message?.toLowerCase() || '';
        return (
            message.includes('cors') ||
            message.includes('network error') ||
            message.includes('failed to fetch') ||
            error.code === 'ERR_NETWORK' ||
            error.code === 'ECONNABORTED'
        );
    }
    return false;
};

/**
 * Check if error is a preflight OPTIONS failure
 */
const isPreflightError = (error: AxiosError): boolean => {
    if (error.config?.method?.toUpperCase() === 'OPTIONS') {
        return true;
    }
    // Check if the original request was blocked by CORS
    if (error.response?.status === 0 || (!error.response && error.request)) {
        return true;
    }
    return false;
};

/**
 * Extract error message from API response
 */
const getErrorMessage = (error: AxiosError): string => {
    if (error.response?.data) {
        const data = error.response.data as any;
        
        // Handle Drupal error format
        if (data.message) {
            return data.message;
        }
        if (data.error) {
            return typeof data.error === 'string' ? data.error : data.error.message || 'An error occurred';
        }
        if (typeof data === 'string') {
            return data;
        }
    }
    
    // Network/CORS errors
    if (isCorsError(error)) {
        return 'CORS error: Check backend CORS configuration';
    }
    
    return error.message || 'Network error occurred';
};

/**
 * Request Interceptor
 * 
 * Adds authentication token and custom headers to all API requests.
 * 
 * This implements Step 3 of the Azure AD SSO authentication flow:
 * - Uses access_token obtained from Step 2 (SSO endpoint)
 * - Adds Bearer token to Authorization header: Authorization: Bearer {access_token}
 * - All protected API endpoints will validate this token
 * 
 * Token Source:
 * - Reads access_token from auth store (Zustand)
 * - Auth store is populated after successful SSO login
 * - Token is stored in localStorage and synced to store
 * 
 * All API requests automatically include:
 * - Authorization: Bearer {access_token}
 * - X-Request-Time: ISO timestamp
 * - X-Client-Version: Application version
 */
/**
 * Request Interceptor
 * 
 * Adds authentication token and custom headers to all API requests.
 * Proactively refreshes token if it's about to expire.
 * 
 * This implements Step 3 of the Azure AD SSO authentication flow:
 * - Uses access_token obtained from Step 2 (SSO endpoint)
 * - Adds Bearer token to Authorization header: Authorization: Bearer {access_token}
 * - All protected API endpoints will validate this token
 * 
 * Token Source:
 * - Reads access_token from auth store (Zustand)
 * - Auth store is populated after successful SSO login
 * - Token is stored in localStorage and synced to store
 * 
 * Proactive Token Refresh:
 * - Checks if token is expiring soon (within 5 minutes)
 * - Refreshes token proactively before it expires
 * - Prevents unnecessary 401 errors
 * 
 * All API requests automatically include:
 * - Authorization: Bearer {access_token}
 * - X-Request-Time: ISO timestamp
 * - X-Client-Version: Application version
 */
export const requestInterceptor = async (
    config: InternalAxiosRequestConfig & DrupalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig & DrupalAxiosRequestConfig> => {
    // Get current token from store
    let token = useAuthStore.getState()?.accessToken;
    
    // Proactive token refresh: Check if token is expiring soon (within 5 minutes)
    // Only refresh if token exists and is about to expire
    const requestConfig = config as DrupalAxiosRequestConfig;
    const skipTokenRefresh = (requestConfig as any).skipTokenRefresh;
    if (token && !skipTokenRefresh) {
        // Skip refresh check for refresh endpoint itself to prevent loops
        const isRefreshEndpoint = config.url?.includes('/auth/refresh');
        
        if (!isRefreshEndpoint) {
            if (isTokenExpiringSoon(token, 5)) {
                if (import.meta.env.DEV) {
                    console.log('🔄 Token expiring soon, refreshing proactively...');
                }
                
                // Attempt proactive refresh
                const newToken = await refreshAccessToken();
                if (newToken) {
                    token = newToken;
                } else if (isTokenExpired(token)) {
                    // Token is already expired and refresh failed
                    if (import.meta.env.DEV) {
                        console.warn('🔒 Token expired and refresh failed');
                    }
                    // Will be handled by 401 error interceptor
                }
            }
        }
    }
    
    // Add authentication token to request
    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    // Add custom headers
    if (config.headers) {
        config.headers['X-Request-Time'] = new Date().toISOString();
        config.headers['X-Client-Version'] = window._env_.AIPORTAL_APP_VERSION || '1.0.0';
        config.headers['Accept'] = 'text/event-stream';
        // Origin header is automatically set by browser based on window.location.origin
        // When served by FastAPI on localhost:8000, Origin will be http://localhost:8000
    }

    // Log request in development mode only
    if (import.meta.env.DEV) {
        const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
        const method = config.method?.toUpperCase() || 'GET';
        console.log(`📤 ${method} ${fullUrl}`, {
            headers: config.headers,
            params: config.params,
        });
    }

    return config;
};

/**
 * Request Error Interceptor
 * Handles errors that occur before the request is sent
 */
export const requestErrorInterceptor = (error: AxiosError): Promise<AxiosError> => {
    if (import.meta.env.DEV) {
        console.error('❌ Request Setup Error:', error.message, error);
    }
    
    // Only show notification if not a CORS error (CORS errors are handled in error interceptor)
    if (!isCorsError(error)) {
        enqueueSnackbar('Failed to setup request', { variant: 'error' });
    }
    
    return Promise.reject(error);
};

/**
 * Response Interceptor
 * Logs successful responses in development mode
 */
export const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
    // Log response in development mode only
    if (import.meta.env.DEV) {
        const fullUrl = response.config.baseURL 
            ? `${response.config.baseURL}${response.config.url}` 
            : response.config.url;
        const method = response.config.method?.toUpperCase() || 'GET';
        console.log(`📥 ${method} ${response.status} ${fullUrl}`, {
            data: response.data,
            headers: response.headers,
        });
    }

    return response;
};

/**
 * Error Interceptor
 * Handles all HTTP errors with proper status code handling
 * 
 * Important: This runs AFTER retry interceptor (interceptors execute in reverse order)
 */
export const errorInterceptor = async (
    error: AxiosError
): Promise<AxiosResponse | never> => {
    const { response, request, config } = error;
    const requestConfig = config as InternalAxiosRequestConfig & DrupalAxiosRequestConfig;

    // Handle CORS errors - NEVER retry these
    if (isCorsError(error) || isPreflightError(error)) {
        if (import.meta.env.DEV) {
            console.error('🚫 CORS Error (not retryable):', {
                message: error.message,
                code: error.code,
                url: config?.url,
            });
        }
        
        // Don't show notification for CORS errors if skipErrorNotification is set
        // if (!requestConfig?.skipErrorNotification) {
        //     enqueueSnackbar(
        //         'CORS error: Please check backend CORS configuration or network connection.',
        //         {
        //             variant: 'error',
        //             autoHideDuration: 2000,
        //         }
        //     );
        // }
        
        return Promise.reject(error);
    }

    // Network error (no response from server)
    if (!response) {
        if (!request) {
            // Request setup failed
            if (import.meta.env.DEV) {
                console.error('❌ Request Setup Error:', error.message);
            }
            
            if (!requestConfig?.skipErrorNotification) {
                enqueueSnackbar('Failed to setup request', { variant: 'error' });
            }
        } else {
            // Network error - no response received
            // Check if it's a timeout error
            const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
            
            if (import.meta.env.DEV) {
                console.error(`❌ Network Error: ${error.message}`, { 
                    code: error.code,
                    isTimeout 
                });
            }
            
            if (!requestConfig?.skipErrorNotification) {
                const message = isTimeout 
                    ? 'Request timed out. The server is taking longer than usual to respond. Please try again.'
                    : 'Network error. Please check your connection.';
                enqueueSnackbar(message, {
                    variant: 'error',
                    autoHideDuration: 6000,
                });
            }
        }
        return Promise.reject(error);
    }

    // Response received with error status
    const status = response.status;
    const errorMessage = getErrorMessage(error);
    const url = config?.url || 'unknown';

    // Log error in development (unless skipErrorLogging is set)
    if (import.meta.env.DEV && !requestConfig?.skipErrorLogging) {
        console.error(`❌ ${status} Error on ${url}:`, {
            status,
            message: errorMessage,
            data: response.data,
            headers: response.headers,
        });
    }

    // Handle specific status codes
    switch (status) {
        case 400:
            // Bad Request
            if (!requestConfig?.skipErrorNotification) {
                enqueueSnackbar(errorMessage || 'Invalid request', { variant: 'error' });
            }
            break;

        case 401:
            // Unauthorized - Try to refresh token first, then retry the original request
            const originalRequest = config as InternalAxiosRequestConfig & { _retry?: boolean };
            
            // Skip refresh if this is already a retry or if it's the refresh endpoint itself
            if (originalRequest._retry || originalRequest.url?.includes('/auth/refresh')) {
                // Refresh failed or this is the refresh endpoint - clear auth and redirect
                if (import.meta.env.DEV) {
                    console.warn('🔒 Token refresh failed or refresh endpoint error - clearing auth');
                }
                
                if (!requestConfig?.skipErrorNotification) {
                    // Only show message if not already on login page
                    if (!window.location.pathname.includes('/login')) {
                        enqueueSnackbar('Your session token has expired. Please login again.', {
                            variant: 'warning',
                            autoHideDuration: 4000,
                        });

                        // Redirect to login
                        setTimeout(() => {
                            window.location.href = '/login';
                        }, 1000);
                    }
                }
                
                useAuthStore.getState().clearAuth();
                return Promise.reject(error);
            }

            // Check if we have a valid original request
            if (!originalRequest) {
                if (import.meta.env.DEV) {
                    console.error('❌ No original request config available for 401 error');
                }
                return Promise.reject(error);
            }

            // Try to refresh the token
            const refreshToken = useAuthStore.getState()?.refreshToken || localStorage.getItem('refreshToken');
            
            if (import.meta.env.DEV) {
                console.log('🔄 401 Unauthorized - Attempting token refresh...', {
                    hasRefreshToken: !!refreshToken,
                    refreshTokenPreview: refreshToken ? `${refreshToken.substring(0, 20)}...` : 'none',
                    originalUrl: originalRequest.url,
                    originalMethod: originalRequest.method,
                });
            }
            
            if (!refreshToken) {
                // No refresh token available - clear auth and redirect
                if (import.meta.env.DEV) {
                    console.warn('🔒 No refresh token available - clearing auth');
                }
                
                useAuthStore.getState().clearAuth();

                if (!requestConfig?.skipErrorNotification) {
                    // Only show message if not already on login page
                    if (!window.location.pathname.includes('/login')) {
                        enqueueSnackbar('Your session token has expired. Please login again.', {
                            variant: 'warning',
                            autoHideDuration: 4000,
                        });

                        // Redirect to login
                        setTimeout(() => {
                            window.location.href = '/login';
                        }, 1000);
                    }
                }
                
                return Promise.reject(error);
            }

            // We have a refresh token - attempt to refresh
            // Use the centralized refreshAccessToken function to ensure:
            // 1. Multiple simultaneous 401s wait for a single refresh
            // 2. Debouncing prevents too frequent refreshes
            // 3. All pending requests get the same new token
            try {
                if (import.meta.env.DEV) {
                    console.log('🔄 Attempting to refresh token using centralized refresh function...');
                }
                
                // Use the centralized refreshAccessToken function
                // This will handle debouncing and prevent multiple simultaneous refreshes
                // Force refresh since we got a 401 (token is definitely invalid)
                const newAccessToken = await refreshAccessToken(true);
                
                if (!newAccessToken) {
                    if (import.meta.env.DEV) {
                        console.error('❌ Token refresh failed - no new access token returned');
                    }
                    throw new Error('Token refresh failed');
                }

                if (import.meta.env.DEV) {
                    console.log('✅ Token refreshed successfully, updating request headers');
                }

                // Mark this request as a retry to prevent infinite loops
                originalRequest._retry = true;
                
                // Update the original request with new token
                if (!originalRequest.headers) {
                    originalRequest.headers = {} as any;
                }
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                
                // Ensure baseURL is set correctly for retry
                if (!originalRequest.baseURL) {
                    originalRequest.baseURL = axiosInstance.defaults.baseURL || getFastAPIBaseUrl();
                }

                if (import.meta.env.DEV) {
                    console.log('🔄 Retrying original request:', {
                        url: originalRequest.url,
                        baseURL: originalRequest.baseURL,
                        method: originalRequest.method,
                        hasAuthHeader: !!originalRequest.headers.Authorization,
                    });
                }

                // Retry the original request using axiosInstance (so interceptors are applied)
                // IMPORTANT: Return the promise directly - don't let it fall through to reject
                const retryResponse = await axiosInstance(originalRequest);
                
                if (import.meta.env.DEV) {
                    console.log('✅ Original request retried successfully after token refresh:', {
                        status: retryResponse.status,
                        url: originalRequest.url,
                    });
                }
                
                // Return the successful response - this prevents fall-through to reject
                return retryResponse;
                
            } catch (refreshError: any) {
                // Refresh failed or retry failed - clear auth and redirect
                if (import.meta.env.DEV) {
                    console.error('❌ Token refresh or retry failed:', {
                        message: refreshError.message,
                        response: refreshError.response?.data,
                        status: refreshError.response?.status,
                        url: refreshError.config?.url,
                        isRetryError: refreshError.config?.url === originalRequest.url,
                    });
                }
                
                // Determine if this is a refresh failure or retry failure
                // If the error is from the retry (same URL as original request), check status
                // If it's a 401 on retry, refresh failed or token is still invalid
                const isRetryError = refreshError.config?.url === originalRequest.url;
                const isRetry401 = isRetryError && refreshError.response?.status === 401;
                
                // If refreshAccessToken returned null, or retry got 401, we need to logout
                // refreshAccessToken returns null on failure, which we convert to an error
                // So any error here means refresh failed or retry failed with 401
                if (!isRetryError || isRetry401) {
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    useAuthStore.getState().clearAuth();

                    if (!requestConfig?.skipErrorNotification) {
                        // Only show message if not already on login page
                        if (!window.location.pathname.includes('/login')) {
                            enqueueSnackbar('Your session token has expired. Please login again.', {
                                variant: 'warning',
                                autoHideDuration: 4000,
                            });

                            // Redirect to login
                            setTimeout(() => {
                                window.location.href = '/login';
                            }, 1000);
                        }
                    }
                }
                
                return Promise.reject(refreshError);
            }

        case 403:
            // Forbidden - user doesn't have permission
            if (!requestConfig?.skipErrorNotification) {
                enqueueSnackbar(
                    'You do not have permission to perform this action',
                    { variant: 'error', autoHideDuration: 5000 }
                );
            }
            break;

        case 404:
            // Not Found
            if (!requestConfig?.skipErrorNotification) {
                enqueueSnackbar(errorMessage || 'Resource not found', { variant: 'error' });
            }
            break;

        case 409:
            // Conflict
            if (!requestConfig?.skipErrorNotification) {
                enqueueSnackbar(errorMessage || 'Conflict occurred', { variant: 'warning' });
            }
            break;

        case 422:
            // Validation Error
            if (!requestConfig?.skipErrorNotification) {
                enqueueSnackbar(errorMessage || 'Validation failed', { variant: 'error' });
            }
            break;

        case 429:
            // Too Many Requests
            if (!requestConfig?.skipErrorNotification) {
                enqueueSnackbar('Too many requests. Please try again later.', {
                    variant: 'warning',
                    autoHideDuration: 6000,
                });
            }
            break;

        case 500:
        case 502:
        case 503:
        case 504:
            // Server errors - these are handled by retry interceptor
            // Only show notification if retry failed
            if (!requestConfig?.skipErrorNotification) {
                const messages: Record<number, string> = {
                    500: 'Server error. Please try again later.',
                    502: 'Service temporarily unavailable',
                    503: 'Service is under maintenance. Please try again later.',
                    504: 'Request timeout. Please try again.',
                };
                enqueueSnackbar(messages[status] || 'Server error occurred', {
                    variant: status === 503 ? 'warning' : 'error',
                    autoHideDuration: 5000,
                });
            }
            break;

        default:
            // Generic error for other status codes
            if (!requestConfig?.skipErrorNotification) {
                if (status >= 500) {
                    enqueueSnackbar('Server error occurred. Please try again later.', {
                        variant: 'error',
                    });
                } else if (status >= 400) {
                    enqueueSnackbar(errorMessage || 'Request failed', { variant: 'error' });
                }
            }
    }

    // TODO: Send error to monitoring service in production
    // Example: Sentry.captureException(error);

    return Promise.reject(error);
};

/**
 * Token refresh state management
 * Prevents multiple simultaneous refresh attempts
 */
let refreshPromise: Promise<string | null> | null = null;
let lastRefreshTime: number = 0;
const REFRESH_DEBOUNCE_MS = 5000; // 5 seconds debounce between refresh attempts

/**
 * Decode JWT token to check expiration
 */
export const decodeToken = (token: string): { exp?: number; iat?: number; department?: string } | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = JSON.parse(atob(parts[1]));
        return {
            exp: payload.exp,
            iat: payload.iat,
            department: payload.department ?? '',
        };
    } catch (error) {
        return null;
    }
};

/**
 * Check if token is expired or about to expire
 * @param token - JWT access token
 * @param bufferMinutes - Minutes before expiration to consider token as "about to expire" (default: 5)
 */
export const isTokenExpiringSoon = (token: string, bufferMinutes: number = 5): boolean => {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true; // If can't decode, assume expired
    
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const bufferTime = bufferMinutes * 60 * 1000; // Convert buffer to milliseconds
    const now = Date.now();
    
    // Token is expiring soon if expiration is within buffer time
    return expirationTime - now < bufferTime;
};

/**
 * Check if token is already expired
 */
export const isTokenExpired = (token: string): boolean => {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    
    return expirationTime <= now;
};

/**
 * Refresh access token using refresh token
 * Prevents multiple simultaneous refresh attempts with debouncing
 * 
 * This function should be used by both:
 * - Request interceptor (proactive refresh)
 * - Error interceptor (reactive refresh on 401)
 * 
 * It ensures that multiple simultaneous refresh attempts are debounced
 * and all pending requests wait for a single refresh to complete.
 * 
 * @param force - If true, bypasses debounce check (useful when we know token is invalid, e.g., on 401)
 */
export const refreshAccessToken = async (force: boolean = false): Promise<string | null> => {
    // Check if refresh is already in progress
    if (refreshPromise) {
        if (import.meta.env.DEV) {
            console.log('⏳ Token refresh already in progress, waiting...');
        }
        return refreshPromise;
    }

    // Check debounce - don't refresh too frequently
    // Skip debounce if force=true (e.g., when we got a 401 and know token is invalid)
    const now = Date.now();
    if (!force && now - lastRefreshTime < REFRESH_DEBOUNCE_MS) {
        if (import.meta.env.DEV) {
            console.log(`⏳ Token refresh debounced (last refresh was ${Math.round((now - lastRefreshTime) / 1000)}s ago)`);
        }
        // Return current token if refresh was recent
        return useAuthStore.getState()?.accessToken || null;
    }

    const refreshToken = useAuthStore.getState()?.refreshToken;
    if (!refreshToken) {
        if (import.meta.env.DEV) {
            console.warn('🔒 No refresh token available');
        }
        return null;
    }

    // Create refresh promise
    refreshPromise = (async (): Promise<string | null> => {
        try {
            lastRefreshTime = Date.now();
            const fastApiBaseUrl = getFastAPIBaseUrl();
            const currentAccessToken = useAuthStore.getState()?.accessToken || localStorage.getItem('accessToken') || '';
            const { department = '' } = decodeToken(currentAccessToken) ?? {};
            const refreshResponse = await axios.post(
                `${fastApiBaseUrl}/api/v1/auth/refresh`,
                { refresh_token: refreshToken, department },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    withCredentials: true,
                    timeout: 30000,
                    skipTokenRefresh: true, // Skip proactive refresh for refresh endpoint itself
                } as DrupalAxiosRequestConfig
            );
            
            const responseData = refreshResponse.data as any;
            const newAccessToken = responseData.accessToken || responseData.access_token;
            
            if (!newAccessToken) {
                throw new Error('No access token in refresh response');
            }
            
            // Update store with new token
            const currentUser = useAuthStore.getState()?.user;
            if (currentUser && newAccessToken) {
                useAuthStore.getState()?.setAuth?.(currentUser, newAccessToken, refreshToken);
            }

            if (import.meta.env.DEV) {
                console.log('✅ Token refreshed successfully');
            }
            
            return newAccessToken;
        } catch (refreshError: any) {
            if (import.meta.env.DEV) {
                console.warn('🔒 Token refresh failed:', refreshError.message);
            }
            return null;
        } finally {
            // Clear refresh promise after completion
            refreshPromise = null;
        }
    })();

    return refreshPromise;
};


/**
 * Initialize all interceptors
 * 
 * IMPORTANT: Interceptor execution order (reverse of registration):
 * 1. Retry interceptor (registered last, runs first)
 * 2. Error interceptor (registered first, runs second)
 * 
 * This ensures retry logic runs before error handling
 */
let interceptorsInitialized = false;

export const initializeInterceptors = (): void => {
    if (interceptorsInitialized) {
        return;
    }
    interceptorsInitialized = true;

    // Request interceptors
    axiosInstance.interceptors.request.use(requestInterceptor, requestErrorInterceptor);

    // Response interceptors
    // Error interceptor is registered first, so retry interceptor (registered later) will run first
    axiosInstance.interceptors.response.use(responseInterceptor, errorInterceptor);

    if (import.meta.env.DEV) {
        console.log('✅ Axios interceptors initialized');
    }
};
